/**
 * runSubValidator — Sub-validator isolation helper (R7, Sprint 5).
 *
 * Background: prior to this helper, a crash inside any single sub-validator
 * (e.g. `validateNotation` inside the content stage) would propagate up and
 * abort the entire stage, blinding all sibling checks. The pipeline was
 * stage-safe (thanks to `runStage` in the orchestrator) but
 * sub-validator-fragile: one bad seed could take down 4 unrelated checks.
 *
 * This helper wraps each sub-validator call so that a thrown error:
 *   1. Is logged via the central logger (no silent swallow).
 *   2. Emits a single info-level `*_SUBVALIDATOR_CRASHED` issue tagged with
 *      the sub-validator name, so admin diagnostics can see which check
 *      failed without losing the rest of the stage output.
 *   3. Returns an empty array, allowing the calling stage to continue with
 *      the remaining sub-validators.
 *
 * Reference: GOVERNANCE_DIDACTISCH_ARCHITECTUUR_AUDIT_2026.md §6 R7.
 */

import type {
  ValidationIssue,
  IssueDomain,
  PipelineStage,
} from '../issueContract'
import { info } from '../issueContract'
import { error as logError } from '../../../utils/logger'
import { SEVERITY } from '../severityScale'

/**
 * Run a single sub-validator with crash-isolation.
 *
 * @param name    Human-readable identifier (e.g. 'validateNotation') used in
 *                logs and the emitted info-issue.
 * @param domain  Issue domain to attribute the crash-issue to (matches the
 *                calling stage, e.g. 'content' or 'pedagogy').
 * @param stage   Pipeline stage number from `STAGES.*` (e.g. STAGES.CONTENT).
 * @param fn      The sub-validator to invoke.
 * @returns       The sub-validator's issues on success, or a single
 *                `*_SUBVALIDATOR_CRASHED` info-issue on failure.
 */
export function runSubValidator(
  name: string,
  domain: IssueDomain,
  stage: PipelineStage,
  fn: () => ValidationIssue[],
): ValidationIssue[] {
  try {
    return fn()
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    logError(
      `[runSubValidator] Sub-validator "${name}" crashed in stage ${stage} (domain=${domain}):`,
      e,
    )
    // Emit a single info-issue so the crash is visible in admin diagnostics
    // without blocking the rest of the stage from continuing.
    return [
      info(
        domain,
        stage,
        'SUBVALIDATOR_CRASHED',
        `Sub-validator "${name}" crashed — other checks in this stage continued`,
        {
          severity: SEVERITY.TRACE,
          suggestion: `Inspect logs for details. Error: ${message}`,
        },
      ),
    ]
  }
}
